// src/components/pages/EventDetails.jsx

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { EventGetItems } from "../../../redux/createEvent/createEventAction/EventDetailAction";
import { getUserProfile } from "../../../redux/auth/action/userProfileActions";
import { fetchTicketByEventId } from "../../../redux/auth/action/fetchTicketActions";
import Navbar from "../../Layout/Navbar/Navbar";
import Footer from "../../Layout/Footer/Footer";
import { toast, ToastContainer } from "react-toastify";
import { FaTicketAlt } from "react-icons/fa";
import Shimmer from "../../Layout/Shimmer";
import "./EventDetails.css";
import { handleAddToCalendar } from "./addToCalendar";
import ReactGA from "react-ga4";
import VenueTime from "./VenueTime";
import LocationSection from "./LocationSection";
import { sanitizeDescription } from "./sanitizeDescription";
import { setSelectedTicket, setSelectedQuantity } from "../../../redux/auth/action/selectedTicketActions";
import 'react-toastify/dist/ReactToastify.css';

const EventDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const accessToken = localStorage.getItem("access") || sessionStorage.getItem("access");

  // Access data from Redux store
  const { loading: eventItemsLoading, data } = useSelector((state) => state.eventItems);
  const selectedTicket = useSelector((state) => state.selectedTicket.selectedTicket);
  const selectedQuantity = useSelector((state) => state.selectedTicket.ticketQuantity);
  const ticketsData = useSelector((state) => state.tickets || {});
  const { tickets = [], loading: fetchTicketLoading, error: ticketError } = ticketsData;

  // Ref for Tickets Section
  const ticketsSectionRef = useRef(null);

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (id) {
      dispatch(EventGetItems(id));                           // Fetch event details
      if (accessToken) {
        dispatch(getUserProfile());                          // Fetch user profile if access token exists
      }

    }
  }, [accessToken, dispatch, id]);

  useEffect(() => {
    console.log("Fetched event data:", data);
  }, [data]);

  // Fire GA Event when event data is loaded
  useEffect(() => {
    if (data && data.event_name) {
      ReactGA.event({
        category: 'Event',
        action: 'View Event Details',
        label: data.event_name, // Ensure no PII is included
      });

      console.log(`GA Event Sent: View Event Details for "${data.event_name}"`);       // Optionally, log to console for verification
    }
  }, [data]);

  useEffect(() => {
    if (id) {
      console.log("Fetching tickets for event ID:", id);
      dispatch(fetchTicketByEventId(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    console.log("tickets in eventdetails: ", tickets);
  }, [tickets]);

  const defaultImage = "https://via.placeholder.com/300x200.png?text=Default+Image";

  const handleGetTicketsClick = () => {
    const accessToken = localStorage.getItem("access") || sessionStorage.getItem("access");

    // Check if user is logged in
    if (!accessToken) {
      // 1. Store the current path to redirect back to after login
      localStorage.setItem('redirectAfterLogin', location.pathname);

      // 2. Show toast message
      toast.info("Please log in or register before purchasing a ticket.");

      // 3. Redirect to registration page
      navigate('/register'); // Or your actual registration/login route
      return; // Stop further execution
    }

    // --- Existing logic for logged-in users or organizers ---
    // If the user IS logged in, proceed with ticket selection/checkout logic
    // You might want to scroll to the tickets section or open a modal here
    if (ticketsSectionRef.current) {
        ticketsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    } else {
        // Fallback if ref isn't attached yet or no tickets section
        console.warn("Tickets section ref not found for scrolling.");
    }

    // Maybe open a checkout modal or navigate to a checkout page if needed
    // For now, let's just log that the user is logged in
    console.log("User is logged in. Proceeding with ticket flow.");

    // Example: If you have a selected ticket, maybe navigate to checkout
    // if (selectedTicket) {
    //   navigate('/checkout'); // Or open checkout modal
    // } else {
    //   toast.info("Please select a ticket first.");
    // }

    // Existing organizer check (if needed, might conflict with user purchase flow)
    // toast.info("Ticket purchase for organizers is currently unavailable...")
  };

  // Add logging for selectedTicket
  useEffect(() => {
    console.log("Selected Ticket in EventDetails:", selectedTicket);
  }, [selectedTicket]);

  const TicketSection = ({ tickets }) => {
    const dispatch = useDispatch();
    
    const handleSelectTicket = (ticket, quantity = 1) => {
      console.log("Selected ticket:", ticket, "Quantity:", quantity);
      dispatch(setSelectedTicket(ticket));
      dispatch(setSelectedQuantity(quantity));
    };
    
    if (!tickets || tickets.length === 0) {
      return (
        <p className="p-4 bg-gray-50 rounded-lg text-center text-gray-600">
          No tickets available for this event.
        </p>
      );
    }
    
    return (
      <div className="space-y-4">
        {tickets.map((ticket, index) => (
          <div 
            key={ticket.id || ticket.ticket_id || index}
            className="p-4 border rounded-lg hover:shadow-md bg-white transition-shadow duration-200"
          >
            <div className="flex flex-col md:flex-row justify-between">
              <div>
                <h3 className="font-semibold text-lg capitalize">
                  {ticket.ticket_category || 'Standard Ticket'}
                </h3>
                <p className="text-gray-600 mt-1">
                  {ticket.description || 'No description available.'}
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end">
                <span className="text-xl font-bold">
                  {ticket.price === 0 || ticket.ticket_category?.toLowerCase() === 'free' 
                    ? 'Free' 
                    : `£${parseFloat(ticket.price).toFixed(2)}`}
                </span>
                
                <div className="mt-2 flex items-center">
                  <select 
                    className="mr-2 p-2 border rounded"
                    onChange={(e) => handleSelectTicket(ticket, parseInt(e.target.value))}
                  >
                    {[...Array(10)].map((_, i) => (
                      <option key={i} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <button
                    className="px-4 py-2 rounded-full bg-sunday_primary text-white hover:brightness-95"
                    onClick={() => handleSelectTicket(ticket, 1)}
                  >
                    Select
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f2f4f7]">
      <Navbar />

      <ToastContainer position="top-right" containerId={"eventDetails"} autoClose={3000} />
      <div className="flex-grow p-5 flex justify-center">
        <div className="relative w-full max-w-5xl flex flex-col font-poppins">

          {/* Thumbnail Image */}
          {eventItemsLoading && (
            <div className="relative w-full aspect-16/9 overflow-hidden mb-4">
              <Shimmer width="100%" height="500px" borderRadius="rounded-xl" />
            </div>
          )}
          {!eventItemsLoading && (
            <div
              className="relative rounded-xl overflow-hidden mb-4 w-full"
              style={{ paddingBottom: '56.25%' }}
            >
              <img
                src={data?.thumbnail_path || defaultImage}
                alt="Event Thumbnail"
                className="absolute top-0 left-0 w-full h-full object-cover"
              />
            </div>
          )}

          {/* Short date */}
          <VenueTime eventItemsLoading={eventItemsLoading} data={data} short={true} id={id} />

          {/* Event Name */}
          {eventItemsLoading && (
            <Shimmer width="60%" height="48px" borderRadius="rounded" className="mb-3 mt-2" />
          )}
          {!eventItemsLoading && (
            <h1 className="text-5xl font-bold mb-3">{data?.event_name || "Untitled Event"}</h1>
          )}

          {/* Event Category/s */}
          {eventItemsLoading && <Shimmer width="90px" height="35px" borderRadius="rounded-full" />}

          {!eventItemsLoading && (
            <div className="flex items-center w-max py-2 text-white bg-sunday_secondary bg-opacity-85 rounded-full">
              {data?.category_name && (
                <div
                  className={`${data.subcategory_name ? "ps-4" : "px-4"} text-sm`}
                >
                  {data.category_name}
                </div>
              )}

              {/* dot divider */}
              {data?.subcategory_name && (<span className="mx-2 h-[20px]">·</span>)}

              {/* Event Subcategory/s */}
              {data?.subcategory_name && (
                <div className="pe-4 text-sm"
                >
                  {data.subcategory_name}
                </div>
              )}

              {!data?.category_name && (
                <p><i>No categories available</i></p>
              )}
            </div>
          )}

          {/* Short description test */}
          {/* <p className="mb-1 text-sm">
            This is a short description that will be visible above the get tickets button and below the category and sub-category. Lorem ipsum dolor sit amet.
          </p> */}

          {/* Ticket and Calendar Options */}
          {eventItemsLoading ? (
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <Shimmer width="320px" height="48px" borderRadius="rounded-full" />
              <Shimmer width="320px" height="48px" borderRadius="rounded-full" />
            </div>
          ) : (
            <div className="flex flex-col py-3 bg-[#f2f4f7] z-20 sm:flex-row gap-4 sticky top-0">
              <button
                className="bg-[#FF9000] scaling-button w-full max-w-full sm:max-w-xs flex items-center justify-center text-white hover:bg-[#e68200] transition duration-200 py-2 rounded-full"
                onClick={handleGetTicketsClick}
              >
                {selectedTicket?.ticket_category?.toLowerCase() === "free"
                  ? "Check out for Free"
                  : selectedTicket
                    ? `Check out for £${(selectedTicket.price * selectedQuantity).toFixed(2)}`
                    : "Get Tickets"
                } <FaTicketAlt className="ms-2" />
              </button>
              <button
                title="Add the event to your Google Calendar"
                className="bg-transparent scaling-button w-full max-w-full sm:max-w-xs flex items-center justify-center text-[#FF9000] border-2 border-[#FF9000] hover:bg-[#FF9000] hover:text-white transition duration-200 py-2 rounded-full"
                onClick={() => handleAddToCalendar(data)}
                data-tip="Add this event to your Google Calendar"
              >
                Add to Calendar
              </button>
            </div>
          )}

          {/* Organizer company (only shows when event type is internal) */}

          {/* Divider */}
          {/* <div className={data?.event_type === "External" ? "h-[1px] w-full mx-auto mt-4 bg-gray-200" : "h-[1px] w-full mx-auto my-4 bg-gray-200"}></div> */}

          {data?.event_type === "External" ? (null) : (
            <div className="mt-5 px-4 py-3 w-max bg-gray-200 rounded-md flex justify-start items-center opacity-80 text-sm font-poppins">

              <div className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center">
                <img
                  className="object-cover w-full h-full"
                  src="https://lh7-rt.googleusercontent.com/docsz/AD_4nXcTC9Lk1v7Bd5PbcjqlDEKiiqqovqH9SUki4i55_CMzQXZFUKy9plPMdtyksxxfkwv2_EUhhgazPdmjSAMozpdlfYZlpWKQI9n5ehPqwgZlmKU6kz8A5cKaPRuiMt10pQE8T8m9u-_RbVZD2Ac=s412?key=Fns8aJ4mVHSejBZWZKfxdQ"
                  alt="Company logo"
                />
              </div>

              <div className="flex items-center">
                <span className="ms-2 me-1">By</span>
                {eventItemsLoading ? (
                  <Shimmer width="60px" height="20px" borderRadius="rounded" />
                ) : (
                  <span className="font-semibold">
                    {data?.organizer_company ? data.organizer_company : <i>No information</i>}
                  </span>
                )}
              </div>
            </div>)}

          {/* Long date and time */}
          <div>
            <p className="text-xl font-semibold mt-7 mb-1">Date and Time</p>
            <VenueTime eventItemsLoading={eventItemsLoading} data={data} short={false} />
          </div>

          {/* Event Details */}
          <div className="mt-7">
            <p className="text-xl font-semibold mb-2">About this event</p>
            {eventItemsLoading ? (
              <div className="space-y-4">
                <Shimmer width="100%" height="16px" borderRadius="rounded" />
                <Shimmer width="90%" height="16px" borderRadius="rounded" />
                <Shimmer width="95%" height="16px" borderRadius="rounded" />
                <Shimmer width="80%" height="16px" borderRadius="rounded" />
              </div>
            ) : (
              <>
                <VenueTime eventItemsLoading={eventItemsLoading} data={data} duration={true} />

                <div
                  className={`prose event-description text-black leading-relaxed space-y-4 ${isExpanded ? "max-h-full" : "max-h-40 overflow-hidden"
                    } md:max-h-full`}
                  dangerouslySetInnerHTML={{ __html: sanitizeDescription(data?.description) }}
                />
                {/* "Read more" button only on mobile */}
                <button
                  className="text-blue-500 hover:underline md:hidden"
                  onClick={toggleReadMore}
                >
                  {isExpanded ? "Read Less" : "Read More"}
                </button>
              </>
            )}

          </div>

          {/* Location Section */}
          {eventItemsLoading ? (
            <div className="mt-7 space-y-4">
              <Shimmer width="30%" height="24px" borderRadius="rounded" />
              <Shimmer width="100%" height="200px" borderRadius="rounded" />
              <div className="flex justify-around w-full md:w-[550px]">
                {[...Array(4)].map((_, index) => <Shimmer key={index} width="60px" height="60px" borderRadius="rounded-full" />)}
              </div>
            </div>
          ) : (
            <LocationSection data={data} />
          )}

          {/* Tickets Section */}
          {data?.event_type === "External" ? (
            null
          ) : (
            <div ref={ticketsSectionRef} className={data?.extra_image || data?.extra_image2 ? "mt-7" : "mt-7 mb-5"}>
              <p className="text-xl font-semibold mb-4">Tickets</p>
              {fetchTicketLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <Shimmer key={index} width="100%" height="80px" borderRadius="rounded-lg" />
                  ))}
                </div>
              ) : ticketError ? (
                <div className="p-4 bg-red-50 text-red-600 rounded-lg">
                  Error loading tickets: {ticketError}
                </div>
              ) : tickets && tickets.length > 0 ? (
                <TicketSection tickets={tickets} />
              ) : (
                <div className="p-6 bg-gray-50 rounded-lg text-center">
                  <p className="text-gray-600">No tickets available for this event.</p>
                  <p className="text-sm text-gray-500 mt-2">Check back later or contact the event organizer.</p>
                </div>
              )}
            </div>
          )}

          {/* Additional Images */}
          {eventItemsLoading ? (
            <div className="mt-8">
              <p className="text-xl font-semibold mb-2">Additional Images</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Shimmer width="100%" height="200px" borderRadius="rounded-md" />
                <Shimmer width="100%" height="200px" borderRadius="rounded-md" />
              </div>
            </div>
          ) : [data?.extra_image, data?.extra_image2].some(Boolean) && (
            <div className="mt-8">
              <p className="text-xl font-semibold mb-2">Additional Images</p>

              <div className="flex flex-col sm:flex-row gap-4">
                {[data?.extra_image, data?.extra_image2].map((image, index) =>
                  image ? (
                    <img
                      key={index}
                      src={image || defaultImage}
                      alt={`Additional ${index + 1}`}
                      className="rounded-md w-full h-auto max-w-[300px] max-h-[200px] object-cover" />
                  ) : null)}
              </div>
            </div>
          )}

        </div>
      </div >

      <Footer />
    </div >
  );
};

export default EventDetails;
